/** A scheduled Coach Connect call. `date` is the local calendar day, `YYYY-MM-DD`. */
export interface CoachConnectSession {
  date: string;
  title: string;
  desc: string;
  url: string;
}

/** A session with the display values the page renders. */
export interface CoachConnectSessionView {
  /** Short month, e.g. "Oct". */
  mon: string;
  /** Day of month without padding, e.g. "7". */
  day: string;
  /** Full month name — the filter tab label it belongs to. */
  month: string;
  title: string;
  desc: string;
  /** e.g. "October 7 · 10 AM CST". */
  when: string;
  time: string;
  /** e.g. "Oct 7". */
  short: string;
  url: string;
}

export const COACH_CONNECT_SESSIONS: CoachConnectSession[] = [
  {
    date: "2026-09-30",
    title: "Setting Your Budget for 2027",
    desc: "",
    url: "https://mycertainpath.zoom.us/meeting/register/jEPm7zT4QrK0ybHU6BcoFw",
  },
  {
    date: "2026-10-01",
    title: "Discipline in Life and Business",
    desc: "Why a disciplined approach to anything is important and how to become more disciplined.",
    url: "https://mycertainpath.zoom.us/meeting/register/QvHuk6mhSLW8p9ZJU2V2Zg",
  },
  {
    date: "2026-10-07",
    title: "Payment vs. Price: Closing More Sales With Financing",
    desc: 'A fresh look at "including" financing on all your jobs rather than "offering" it. Get the latest intel on financing trends, close more sales and increase revenue.',
    url: "https://mycertainpath.zoom.us/meeting/register/8NinPAmMSTep-8t2PG79Hw",
  },
  {
    date: "2026-10-08",
    title: "Marketing: 3 Rs — Reputation, Reviews & Referrals",
    desc: "When the phones slow down, trust is your best marketing asset. We'll cover building your review pipeline and turning happy customers into referrals.",
    url: "https://mycertainpath.zoom.us/meeting/register/dSiFovWyQbeXVLI_qa3AHQ",
  },
  {
    date: "2026-10-21",
    title: "Building a Culture People Don't Want to Leave",
    desc: "Culture starts from the top: core values, mission and vision. How you hire, fire, train, reward and promote all support the strength of your culture.",
    url: "https://mycertainpath.zoom.us/meeting/register/l17qO5_1Rm67DV35wT7D_g",
  },
  {
    date: "2026-10-22",
    title:
      "Recruiting Pt. 1: Stop Hiring Out of Desperation — Build Your Recruiting Machine",
    desc: "Make recruiting a weekly habit, not a reaction to someone quitting. Where to source beyond job boards, how much time leaders should spend recruiting, and how to turn your whole team into recruiters — so you build a pipeline before you have an empty truck, phone or desk.",
    url: "https://mycertainpath.zoom.us/meeting/register/TylOmPyyROWYpgo3f8a21A",
  },
  {
    date: "2026-10-28",
    title: "AI & Price Transparency",
    desc: "How AI is making price transparency more important than ever — and how to use it to set your company apart.",
    url: "https://mycertainpath.zoom.us/meeting/register/neS-bYLMSGWTX8p6G-Qz4g",
  },
  {
    date: "2026-10-29",
    title: "DMER/Budgets",
    desc: "How to use the DMER/Budget tool and set it up for 2027.",
    url: "https://mycertainpath.zoom.us/meeting/register/YVOfQgWESwK-OV0fnhUsEQ",
  },
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const ALL_MONTHS = "All";

/** Calendar-day key (YYYYMMDD as a number) for comparing local days. */
function dayKey(year: number, monthIndex: number, day: number): number {
  return year * 10000 + (monthIndex + 1) * 100 + day;
}

function toView(session: CoachConnectSession): CoachConnectSessionView {
  const [, m, d] = session.date.split("-").map(Number);
  const month = MONTH_NAMES[m - 1];
  const mon = month.slice(0, 3);
  const day = String(d);
  return {
    mon,
    day,
    month,
    title: session.title,
    desc: session.desc,
    when: `${month} ${day} · 10 AM CST`,
    time: "10 AM CST",
    short: `${mon} ${day}`,
    url: session.url,
  };
}

/**
 * Sessions on `now`'s local calendar day or later, in date order, with their
 * display values.
 */
export function upcomingSessions(
  now: Date,
  sessions: CoachConnectSession[] = COACH_CONNECT_SESSIONS,
): CoachConnectSessionView[] {
  const today = dayKey(now.getFullYear(), now.getMonth(), now.getDate());
  return sessions
    .filter((s) => {
      const [y, m, d] = s.date.split("-").map(Number);
      return dayKey(y, m - 1, d) >= today;
    })
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(toView);
}

/** "All" plus one tab per month that still has an upcoming session. */
export function monthFilters(sessions: CoachConnectSessionView[]): string[] {
  return [ALL_MONTHS, ...Array.from(new Set(sessions.map((s) => s.month)))];
}
